/**
 * iter-170aj — Smart Alerts manager + recent events feed.
 *
 * Two stacked sub-cards inside the same LockedSection:
 *   1. Rules CRUD (create / list / toggle / delete + "Evaluar ahora").
 *   2. Recent events feed (last 25, mark-as-read inline).
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Bell, Plus, Trash2, Loader2, Play, CheckCircle2 } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";
import { api } from "../lib/api";
import { toast } from "sonner";

function fmtDateTime(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(
      lang === "es" ? "es-CO" : lang === "pt" ? "pt-BR" : "en-US",
      { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" },
    );
  } catch { return iso; }
}

export function SmartAlertsCard() {
  const { lang } = useLang();
  const qc = useQueryClient();

  const [name, setName] = useState("");
  const [conditionType, setConditionType] = useState("cash_below");
  const [threshold, setThreshold] = useState("");
  const [currency, setCurrency] = useState("");
  const [factor, setFactor] = useState("2");
  const [accountId, setAccountId] = useState("");

  const { data: rulesData } = useQuery({
    queryKey: ["smart-alerts-rules"],
    queryFn: async () => (await api.get("/smart-alerts/rules")).data,
    retry: false,
  });
  const { data: events } = useQuery({
    queryKey: ["smart-alerts-events"],
    queryFn: async () => (await api.get("/smart-alerts/events?limit=25")).data,
    retry: false,
  });
  const rules = rulesData?.items || [];
  const eventList = events?.items || [];

  const createMut = useMutation({
    mutationFn: async (body) => (await api.post("/smart-alerts/rules", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-alerts-rules"] });
      setName(""); setThreshold(""); setCurrency(""); setAccountId("");
      toast.success(tx(lang, "Rule created", "Regla creada", "Regra criada"));
    },
    onError: () => toast.error(tx(lang, "Create failed", "Error al crear", "Erro ao criar")),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, body }) =>
      (await api.put(`/smart-alerts/rules/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["smart-alerts-rules"] }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/smart-alerts/rules/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-alerts-rules"] });
      toast.success(tx(lang, "Rule deleted", "Regla eliminada", "Regra excluída"));
    },
  });

  const evaluateMut = useMutation({
    mutationFn: async () => (await api.post("/smart-alerts/evaluate-now")).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["smart-alerts-events"] });
      qc.invalidateQueries({ queryKey: ["smart-alerts-rules"] });
      toast.success(tx(lang,
        `Evaluated · ${d.triggered} triggered`,
        `Evaluadas · ${d.triggered} disparadas`,
        `Avaliadas · ${d.triggered} disparadas`));
    },
  });

  const readMut = useMutation({
    mutationFn: async (id) => (await api.post(`/smart-alerts/events/${id}/read`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["smart-alerts-events"] }),
  });

  const submitCreate = () => {
    if (!name.trim()) return;
    const params = {};
    if (conditionType === "cash_below") {
      params.threshold = Number(threshold) || 0;
      params.currency = currency.toUpperCase() || "USD";
    } else if (conditionType === "expense_spike") {
      params.factor = Number(factor) || 2;
    } else if (conditionType === "balance_below") {
      params.account_id = accountId.trim();
      params.threshold = Number(threshold) || 0;
    }
    createMut.mutate({
      name: name.trim(),
      condition_type: conditionType,
      params,
      channels: ["web"],
      enabled: true,
    });
  };

  return (
    <div className="space-y-4">
      {/* Rules CRUD */}
      <Card className="p-5 rounded-2xl space-y-4" data-testid="smart-alerts-rules">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-slate-700" />
            <h3 className="font-semibold text-slate-900">
              {tx(lang, "Alert rules", "Reglas de alerta", "Regras de alerta")}
            </h3>
          </div>
          <Button
            size="sm" variant="outline"
            onClick={() => evaluateMut.mutate()}
            disabled={evaluateMut.isPending}
            className="gap-1.5"
            data-testid="smart-alerts-evaluate-now"
          >
            {evaluateMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            {tx(lang, "Evaluate now", "Evaluar ahora", "Avaliar agora")}
          </Button>
        </div>

        {/* Create form */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 p-3 rounded-xl border border-slate-200 bg-slate-50">
          <Input
            placeholder={tx(lang, "Rule name", "Nombre", "Nome")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="smart-alerts-new-name"
          />
          <select /* design-system: ALLOW legacy-todo */
            value={conditionType}
            onChange={(e) => setConditionType(e.target.value)}
            data-testid="smart-alerts-new-condition"
            className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
          >
            <option value="cash_below">{tx(lang, "Total cash below…", "Caja total bajo…", "Caixa total abaixo…")}</option>
            <option value="expense_spike">{tx(lang, "Expense spike", "Pico de gasto", "Pico de gasto")}</option>
            <option value="balance_below">{tx(lang, "Account below…", "Cuenta bajo…", "Conta abaixo…")}</option>
          </select>
          {conditionType === "cash_below" && (
            <>
              <Input
                placeholder={tx(lang, "Threshold", "Umbral", "Limite")}
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                data-testid="smart-alerts-new-threshold"
              />
              <Input
                placeholder={tx(lang, "Currency", "Moneda", "Moeda")}
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                maxLength={3}
                data-testid="smart-alerts-new-currency"
              />
            </>
          )}
          {conditionType === "expense_spike" && (
            <Input
              placeholder={tx(lang, "Factor (e.g. 2)", "Factor (ej: 2)", "Fator (ex: 2)")}
              type="number"
              step="0.1"
              value={factor}
              onChange={(e) => setFactor(e.target.value)}
              className="md:col-span-2"
              data-testid="smart-alerts-new-factor"
            />
          )}
          {conditionType === "balance_below" && (
            <>
              <Input
                placeholder={tx(lang, "Account ID", "ID cuenta", "ID conta")}
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                data-testid="smart-alerts-new-accountid"
              />
              <Input
                placeholder={tx(lang, "Threshold", "Umbral", "Limite")}
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                data-testid="smart-alerts-new-threshold-b"
              />
            </>
          )}
          <Button
            onClick={submitCreate}
            disabled={!name.trim() || createMut.isPending}
            className="md:col-span-4 gap-1.5"
            data-testid="smart-alerts-create-btn"
          >
            <Plus className="h-3.5 w-3.5" />
            {tx(lang, "Add rule", "Agregar regla", "Adicionar regra")}
          </Button>
        </div>

        {/* List */}
        {rules.length === 0 ? (
          <p className="text-xs text-slate-500 italic py-4 text-center" data-testid="smart-alerts-empty">
            {tx(lang, "No rules yet.", "Aún no hay reglas.", "Ainda sem regras.")}
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="smart-alerts-list">
            {rules.map((r) => (
              <li
                key={r.id}
                className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white"
                data-testid={`smart-alerts-rule-${r.id}`}
              >
                <Switch
                  checked={r.enabled}
                  onCheckedChange={(v) => updateMut.mutate({
                    id: r.id, body: { ...r, enabled: !!v },
                  })}
                  data-testid={`smart-alerts-toggle-${r.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900">{r.name}</span>
                    <Badge variant="outline" className="text-[10px] font-mono">{r.condition_type}</Badge>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5 font-mono">
                    {JSON.stringify(r.params)}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {tx(lang, "Last triggered", "Último disparo", "Último disparo")}: {fmtDateTime(r.last_triggered_at, lang) || "—"}
                  </p>
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={() => deleteMut.mutate(r.id)}
                  className="gap-1.5 rounded-full text-rose-700 border-rose-200 hover:bg-rose-50"
                  data-testid={`smart-alerts-delete-${r.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Events feed */}
      <Card className="p-5 rounded-2xl" data-testid="smart-alerts-events">
        <h3 className="font-semibold text-slate-900 mb-3">
          {tx(lang, "Recent alerts", "Alertas recientes", "Alertas recentes")}
        </h3>
        {eventList.length === 0 ? (
          <p className="text-xs text-slate-500 italic py-4 text-center">
            {tx(lang, "No alerts yet.", "Sin alertas aún.", "Sem alertas ainda.")}
          </p>
        ) : (
          <ul className="space-y-2">
            {eventList.map((ev) => (
              <li
                key={ev.id}
                className={`p-3 rounded-xl border ${
                  ev.read_at ? "border-slate-200 bg-slate-50 opacity-70" : "border-amber-200 bg-amber-50"
                }`}
                data-testid={`smart-alerts-event-${ev.id}`}
              >
                <div className="flex items-start gap-2">
                  <Bell className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-semibold text-slate-900">{ev.title}</span>
                      <span className="text-[10px] text-slate-400 tabular-nums">{fmtDateTime(ev.delivered_at, lang)}</span>
                    </div>
                    <p className="text-xs text-slate-700">{ev.body}</p>
                  </div>
                  {!ev.read_at && (
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => readMut.mutate(ev.id)}
                      className="h-7 px-2 gap-1 text-emerald-700"
                      data-testid={`smart-alerts-event-read-${ev.id}`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
